import { defineStore } from 'pinia'
import { consola } from 'consola'
import { useUserProfileStore } from '@entities/user'
import { useInstitutionStore } from '@entities/institution'
import apiAuth, { apiAdminAuth, logoutUser, sendUserVerificationEmail, useSessionManager } from '~/entities/auth'
import { useTokenManager, useTokenRefreshV2, useLogoutHandler } from '~/features/auth/model'
import { rateLimiter, retryStrategy, offlineManager, getClientFingerprint } from '~/shared/lib/auth'

import type {
  LoginParams,
  adminLoginParams,
  PhoneVerificationParams,
  LoginV2Params,
  RegisterV2Params,
  AuthV2Response,
} from '~/entities/auth'

const logger = consola.withTag('auth-store')

const initPushNotifications = async (): Promise<void> => {
  if (!process.client) return

  try {
    logger.info('Инициализация push notifications...')
    const pushSettingsModule = await import('~/features/notifications/@x/auth')
    const pushSettings = pushSettingsModule.usePushNotificationSettings()
    await pushSettings.initPushNotificationsAfterAuth()
    logger.success('Push notifications инициализированы')
  } catch (error) {
    logger.warn('Ошибка инициализации push notifications:', error)
  }
}

/**
 * Откладывает показ пуш-промпта до конца онбординга новичка.
 * Промпт всплывёт, когда пользователь добавит адрес доставки или нажмёт «Не сейчас»,
 * а не сразу после авторизации (см. usePushNotificationInit / usePushNotificationSettings).
 */
const markOnboardingPushPending = async (): Promise<void> => {
  if (!process.client) return

  try {
    const pushSettingsModule = await import('~/features/notifications/@x/auth')
    pushSettingsModule.usePushNotificationSettings().markOnboardingPushPending()
  } catch (error) {
    logger.warn('Не удалось отложить пуш-промпт до онбординга:', error)
  }
}

const checkRateLimit = (action: 'login' | 'register'): void => {
  if (!rateLimiter.canAttempt(action)) {
    const remainingMs = rateLimiter.getBlockedRemainingTime(action)
    const remainingSec = Math.ceil(remainingMs / 1000)
    throw new Error(`Слишком много попыток ${action === 'login' ? 'входа' : 'регистрации'}. Попробуйте через ${remainingSec} сек`)
  }
}

const logSuccessfulAuth = (action: 'login' | 'register'): void => {
  logger.success(`Успешная ${action === 'login' ? 'авторизация' : 'регистрация'} через API V2`)
  logger.info('Access token сохранен в localStorage')
  logger.info('Refresh token сохранен в localStorage')
  logger.info('Session ID сохранен в localStorage')
  logger.info('Данные закешированы для offline режима')
}

const saveAuthData = async (
  data: AuthV2Response,
  sessionManager: ReturnType<typeof useSessionManager>,
  userProfileStore: ReturnType<typeof useUserProfileStore>,
  logoutHandler: ReturnType<typeof useLogoutHandler>,
): Promise<void> => {
  const currentTime = new Date().toISOString()
  logger.info('🕐 Сохранение токенов:', {
    currentTime,
    currentTimestamp: Date.now(),
    accessToken: data.accessToken?.slice(0, 30) + '...',
    refreshToken: data.refreshToken?.slice(0, 30) + '...',
    sessionId: data.sessionId?.slice(0, 20) + '...',
  })

  await sessionManager.setSession(data.accessToken, data.sessionId, data.refreshToken)
  logoutHandler.handleSuccessfulLogin()

  offlineManager.cacheTokens({
    accessToken: data.accessToken,
    sessionId: data.sessionId,
  })

  await userProfileStore.fetchUser()

  if (userProfileStore.user) {
    offlineManager.cacheUser(userProfileStore.user)
  }
}

const isPendingVerification = (data: any): boolean => {
  return !data.accessToken && data.message && data.user?.id
}

export const useAuthStore = defineStore('auth', () => {
  const userProfileStore = useUserProfileStore()
  const sessionManager = useSessionManager()
  useTokenManager()
  const tokenRefreshV2 = useTokenRefreshV2()
  const logoutHandler = useLogoutHandler()

  const login = async (params: LoginParams): Promise<AuthV2Response> => {
    const storeInstitution = useInstitutionStore()
    checkRateLimit('login')

    try {
      logger.info('Вход через API V2 с системой защиты')
      const data = await retryStrategy.execute(async () => {
        const loginParamsV2: LoginV2Params = {
          emailOrPhone: params.phone,
          password: params.password,
          institutionId: String(params.institution_id || (storeInstitution.institution as any)?.id || useRuntimeConfig().public.institutionID),
          sessionId: sessionManager.authSessionId.value || undefined,
          clientFingerprint: await getClientFingerprint(),
        }
        const response = await apiAuth.login(loginParamsV2)
        return response.data
      })

      await saveAuthData(data, sessionManager, userProfileStore, logoutHandler)
      logSuccessfulAuth('login')
      rateLimiter.reset('login')
      await initPushNotifications()

      if (process.client) {
        try {
          const { useTabsSync, TabSyncEventType } = await import('@shared/lib')
          const tabsSync = useTabsSync()
          tabsSync.broadcast(TabSyncEventType.USER_LOGGED_IN, {
            userId: data.user.id,
            sessionId: data.sessionId,
          })
          logger.info('📤 Уведомление об авторизации отправлено в другие вкладки')
        } catch (error) {
          logger.warn('Не удалось отправить уведомление об авторизации:', error)
        }
      }

      return data
    } catch (error: any) {
      rateLimiter.recordAttempt('login')
      logger.error('Ошибка авторизации:', error?.data?.message || error?.message || error)
      throw error
    }
  }

  const adminLogin = async (params: adminLoginParams): Promise<any> => {
    try {
      logger.info('Вход администратора через V2 API')
      const { useAdminSessionManager } = await import('./useAdminSessionManager')
      const adminSessionManager = useAdminSessionManager()
      const data = await apiAdminAuth.login({
        email: params.email,
        password: params.password,
      })
      adminSessionManager.setAdminSession(data.adminAccessToken, data.adminRefreshToken)

      if (userProfileStore) {
        const adminUser = {
          id: Number.parseInt(data.user.id),
          email: data.user.email,
          institution_id: Number.parseInt(data.user.institution_id),
          role: data.user.role,
          firstName: data.user.email.split('@')[0],
          lastName: '',
          phoneNumber: '',
        }
        userProfileStore.user = adminUser as any
      }

      logger.success('✅ Администратор успешно авторизован через V2')
      logger.info('Admin access token сохранен в localStorage')
      logger.info('Admin refresh token сохранен в localStorage')
      return data
    } catch (error: any) {
      logger.error('❌ Ошибка авторизации администратора:', error)
      throw error
    }
  }

  const register = async (params: any): Promise<any> => {
    const storeInstitution = useInstitutionStore()
    checkRateLimit('register')

    try {
      logger.info('Регистрация через API V2 с системой защиты')
      const data = await retryStrategy.execute(async () => {
        const registerParamsV2: RegisterV2Params = {
          firstName: params.firstName || '',
          lastName: params.lastName || '',
          email: params.email || '',
          phoneNumber: params.phone,
          password: params.password,
          institutionId: String(params.institution_id || (storeInstitution.institution as any)?.id || useRuntimeConfig().public.institutionID),
          clientFingerprint: await getClientFingerprint(),
        }
        const response = await apiAuth.register(registerParamsV2)
        return response.data
      })

      // Регистрация удалась — пуш-промпт показываем только в финале онбординга
      // (после добавления адреса / «Не сейчас»), а не сразу. Флаг ставим до сохранения
      // токенов и до подтверждения телефона, чтобы авто-показ точно был подавлен.
      await markOnboardingPushPending()

      if (isPendingVerification(data)) {
        logger.info('✅ Регистрация успешна, требуется подтверждение через Telegram')
        logger.info('📱 User ID:', data.user.id)
        rateLimiter.reset('register')
        return {
          requiresVerification: true,
          // Признак и ссылка подтверждения через MAX (Сценарий B). Если бэк отдал их —
          // Registration покажет модалку MAX вместо Telegram.
          requiresMaxVerification: !!data.requiresMaxVerification,
          maxVerifyLink: data.maxVerifyLink,
          user: data.user,
          message: data.message,
        }
      }

      await saveAuthData(data, sessionManager, userProfileStore, logoutHandler)
      logSuccessfulAuth('register')
      rateLimiter.reset('register')
      await initPushNotifications()

      if (process.client) {
        try {
          const { useApplyReferralToken } = await import('~/features/profile/@x/auth')
          await useApplyReferralToken()
        } catch (error) {
          logger.warn('Ошибка применения реферального кода (не критично):', error)
        }
      }

      if (process.client) {
        try {
          const { useTabsSync, TabSyncEventType } = await import('@shared/lib')
          const tabsSync = useTabsSync()
          tabsSync.broadcast(TabSyncEventType.USER_LOGGED_IN, {
            userId: data.user.id,
            sessionId: data.sessionId,
            isRegistration: true,
          })
          logger.info('📤 Уведомление о регистрации отправлено в другие вкладки')
        } catch (error) {
          logger.warn('Не удалось отправить уведомление о регистрации:', error)
        }
      }

      return {
        requiresVerification: false,
        ...data,
      }
    } catch (error: any) {
      rateLimiter.recordAttempt('register')
      logger.error('Ошибка регистрации:', error?.data?.message || error?.message || error)
      throw error
    }
  }

  const logout = async (): Promise<void> => {
    logger.info('🚪 Выход из системы (через AuthLogoutService)')
    await logoutUser('useAuthStore.logout')
  }

  const adminlogout = async (): Promise<void> => {
    logger.info('🚪 Выход администратора через V2 API')
    try {
      const response = await apiAdminAuth.logout()
      logger.success('✅ Серверная сессия инвалидирована:', response.message)
    } catch (error) {
      logger.warn('⚠️ Ошибка вызова API logout (продолжаем разлогинивание):', error)
    }

    const { useAdminSessionManager } = await import('./useAdminSessionManager')
    const adminSessionManager = useAdminSessionManager()
    adminSessionManager.clearAdminSession()

    if (userProfileStore) {
      userProfileStore.user = null
    }

    logger.success('✅ Админская сессия очищена')
    if (process.client) {
      navigateTo('/administration/home')
    }
  }

  const refreshAccessToken = async (): Promise<string | null> => {
    try {
      logger.info('Обновление токена через V2')
      const newToken = await tokenRefreshV2.refreshToken()
      if (!newToken) {
        logger.warn('Не удалось обновить токен (возможна временная ошибка)')
      }
      return newToken
    } catch (error) {
      logger.error('Ошибка обновления токена:', error)
      return null
    }
  }

  const checkPhoneVerification = async (params: PhoneVerificationParams) => {
    try {
      logger.info('Проверка верификации телефона')
      const { data } = await apiAuth.checkPhoneVerification(params)
      await sessionManager.setSession(data.accessToken, data.sessionId, data.refreshToken)
      await userProfileStore.fetchUser()
      await initPushNotifications()
      logger.success('Телефон успешно верифицирован')
      return data
    } catch (error) {
      logger.error('Ошибка верификации телефона:', error)
      throw error
    }
  }

  const checkMaxVerification = async (params: { phoneNumber: string; institutionId: string | number }) => {
    try {
      logger.info('Проверка подтверждения телефона через MAX')
      const { data } = await apiAuth.checkMaxVerification(params)
      await sessionManager.setSession(data.accessToken, data.sessionId, data.refreshToken)
      await userProfileStore.fetchUser()
      await initPushNotifications()
      logger.success('Телефон подтверждён через MAX')
      return data
    } catch (error) {
      logger.error('Ошибка подтверждения через MAX:', error)
      throw error
    }
  }

  const sendVerificationEmail = async (email: string, institutionId: number) => {
    try {
      logger.info('Отправка письма верификации на', email)
      const data = await sendUserVerificationEmail(email, institutionId)
      logger.success('Письмо верификации отправлено')
      return data
    } catch (error) {
      logger.error('Ошибка отправки письма верификации:', error)
      throw error
    }
  }

  return {
    isLoggedIn: sessionManager.hasActiveSession,
    login,
    adminLogin,
    adminlogin: adminLogin,
    adminlogout,
    register,
    logout,
    refreshAccessToken,
    checkPhoneVerification,
    checkMaxVerification,
    sendVerificationEmail,
    authAccessToken: sessionManager.authAccessToken,
    authRefreshToken: sessionManager.authRefreshToken,
    authSessionId: sessionManager.authSessionId,
  }
})
